import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import Honeybadger from 'honeybadger-js'
import _ from 'underscore'

import CustomersEntryPanel from 'pages/shared/CustomerForm/CustomersEntryPanel'
import CustomerFields from 'pages/shared/CustomerForm/CustomerFields'
import Form from 'pages/shared/Form'

import { findHomeCustomerValuesForDiagCustomerValues } from 'pages/shared/findHomeCustomerValuesForDiagCustomerValues'

import GA4 from 'helpers/ga4'

import { useServerErrorContext } from 'hooks/ServerErrorContext'
import { useStoreContext } from 'hooks/StoreContext'

import useNotificationRecipients from 'hooks/useNotificationRecipients'
import { useConfirmDialog, useAlertDialog } from 'hooks/useDialog'

import CustomerModel from 'models/Customer'

import { screenSize } from 'styles/shared'
import { Customer } from 'types/Customer'
import { CONFIRMATION_DIALOG_MESSAGES } from 'helpers/confirmationDialogMessages'

type propTypes = {
  customer: Customer | CustomerModel
  dealerId: string | number
  readOnly: boolean
  readOnlyMessage: string
  title: string
  displayDeleteButton: boolean
  closeCallback?: () => void
  onSaveCustomerCallback: (id: number) => void
}

const CustomerForm = ({ customer: customerProp, dealerId, readOnly, readOnlyMessage, title, displayDeleteButton = false, closeCallback = () => {}, onSaveCustomerCallback = () => {} }: propTypes) => {
  const customer = customerProp as unknown as Customer
  const navigate = useNavigate()
  const customerModel = useMemo(() => new CustomerModel(), [])

  useNotificationRecipients()

  const { setServerError } = useServerErrorContext()
  const { notificationRecipients, areNotificationRecipientsLoaded } = useStoreContext()
  const { confirm } = useConfirmDialog()
  const { alert } = useAlertDialog()

  const selectedNotificationRecipientIds = customer?.notificationRecipientIds
  const allNotificationRecipientIds = useMemo(() => _.map(notificationRecipients, (recipient) => recipient.id), [notificationRecipients])

  const [customerAttributes, setCustomerAttributes] = useState({ ...customer })
  const [originalAttributes, setOriginalAttributes] = useState({ ...customer })
  const [errors, setErrors] = useState({ firstName: '', lastName: '', zip: '', email: '', phone: '' })
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const onClickCopy = useCallback(() => {
    const attributesToCopy = findHomeCustomerValuesForDiagCustomerValues(customerAttributes)

    setCustomerAttributes({ ...customerAttributes, ...attributesToCopy })
  }, [setCustomerAttributes, customerAttributes])

  useEffect(() => {
    if (!areNotificationRecipientsLoaded || (customer.id)) { return }
    if (customer) {
      const loadedCustomerAttributes = {
        ...customer,
        notificationRecipientIds: 'notificationRecipientIds' in customer ? selectedNotificationRecipientIds : allNotificationRecipientIds
      }
      setCustomerAttributes(loadedCustomerAttributes)
      setOriginalAttributes(loadedCustomerAttributes)
    }
  }, [customer, areNotificationRecipientsLoaded, selectedNotificationRecipientIds, allNotificationRecipientIds])

  const onClickCancel = useCallback(async () => {
    const hasChanges = !_.isEqual(customerAttributes, originalAttributes)
    const isConfirmed = !hasChanges || (hasChanges && (await confirm({
      title: CONFIRMATION_DIALOG_MESSAGES.CANCEL.TITLE,
      description: CONFIRMATION_DIALOG_MESSAGES.CANCEL.DESCRIPTION,
      confirmText: CONFIRMATION_DIALOG_MESSAGES.BUTTON_TEXT.OK
    })))

    if (isConfirmed) {
      const eventData = { category: `${title} Page`, action: 'Click Cancel - Changes Discarded' }

      GA4.sendEvent(eventData)
      closeCallback()
    }
  }, [customerAttributes, originalAttributes, title, confirm, closeCallback])

  const isNewAndUnacknowledged = customerModel.isNewAndUnacknowledged(customer)
  const isUpdatedAndUnacknowledged = customerModel.isUpdatedAndUnacknowledged(customer)
  const isDuplicateAndUnacknowledged = customerModel.isDuplicateAndUnacknowledged(customer)
  const isUpdateAcknowledged = customerModel.isUpdateAcknowledged(customerAttributes)

  const onSaveCallback = useCallback(() => {
    if (readOnly) {
      alert({ title: 'Read-only', description: readOnlyMessage })
      return
    }
    setIsSaving(true)

    const labelSelectedToRemove = customerModel.getLabelSelectedToRemove(customer, customerAttributes)

    if (labelSelectedToRemove) {
      const eventData = {
        category: `${title} Page`,
        action: `Select ${labelSelectedToRemove} Customer acknowledge box and Click Save`
      }

      GA4.sendEvent(eventData)
    }

    const onSaveSuccessCallback = (res) => {
      onSaveCustomerCallback(res.id)
      closeCallback()
    }

    const onValidationErrorCallback = (errors) => {
      setErrors(errors)
      setIsSaving(false)
    }

    const onSaveFailureCallBack = (e) => {
      Honeybadger.notify('Error saving in createCustomerCallback', { context: e })
      setServerError('Failed to save customer')
      setIsSaving(false)
    }

    customerModel.save(dealerId, customer.id, customerAttributes, onSaveSuccessCallback, onSaveFailureCallBack, onValidationErrorCallback)
  }, [alert, closeCallback, customer, customerAttributes, customerModel, dealerId, onSaveCustomerCallback, readOnly, readOnlyMessage, setServerError, title])

  const onDeleteCallback = useCallback(async () => {
    if (readOnly) {
      alert({ title: 'Read-only', description: 'Read-only view: can\'t delete customer' })

      return
    }
    if (await confirm({
      title: 'Delete Customer',
      description: 'Are you sure you want to delete this customer? This cannot be undone.',
      confirmText: 'Delete'
    })) {
      setIsDeleting(true)
      const onDeleteSuccessCallback = () => {
        navigate('/customers?page=1&sortAttribute=customer&sortDirection=asc&view=all')
      }

      const onDeleteFailureCallback = (e) => {
        Honeybadger.notify('Error destroying in deleteCustomerCallback', { context: e })

        setServerError('Failed to delete customer')
        setIsDeleting(false)
      }

      customerModel.delete(dealerId, customer.id, onDeleteSuccessCallback, onDeleteFailureCallback)
      const eventData = { category: `${title} Page`, action: 'Click Delete - Confirmed' }

      GA4.sendEvent(eventData)
    }
  }, [readOnly, confirm, alert, customerModel, dealerId, customer.id, title, navigate, setServerError])

  const onChangeCallback = useCallback((updatedAttribute) => {
    setCustomerAttributes((prev) => ({ ...prev, ...updatedAttribute }))
  }, [])

  const isPiiCustomerUnacknowledged = isNewAndUnacknowledged || isUpdatedAndUnacknowledged || isDuplicateAndUnacknowledged

  const showCustomerEntryPanel = useMemo(() => (
    <CustomersEntryPanel
      {...{ ...customerAttributes, onClickCopy }}
      isNewAndUnacknowledged={isNewAndUnacknowledged}
      isUpdatedAndUnacknowledged={isUpdatedAndUnacknowledged}
      isDuplicateAndUnacknowledged={isDuplicateAndUnacknowledged}
      isUpdateAcknowledged={isUpdateAcknowledged}
      piiHomeDuplicateNames={customer && customer.piiHomeDuplicateNames}
      orginalPiiAcknowledgedAt={originalAttributes.piiAcknowledgedAt}
      onChangeCallback={onChangeCallback}
    />
  ), [customerAttributes, onClickCopy, isNewAndUnacknowledged, isUpdatedAndUnacknowledged, isDuplicateAndUnacknowledged, isUpdateAcknowledged, customer, originalAttributes.piiAcknowledgedAt, onChangeCallback])

  return (
    <S.Container>
      {isPiiCustomerUnacknowledged && showCustomerEntryPanel}
      <S.Form
        onSaveCallback={onSaveCallback}
        onCancelCallback={onClickCancel}
        onDeleteCallback={onDeleteCallback}
        isSaving={isSaving}
        isDeleting={isDeleting}
        displayDeleteButton={displayDeleteButton}
        deleteButtonText="Customer"
        $isPiiCustomerUnacknowledged={isPiiCustomerUnacknowledged}
      >
        <CustomerFields {...{ ...customerAttributes, errors, onChangeCallback }} />
      </S.Form>
    </S.Container>
  )
}

export default CustomerForm

const S = {
  Container: styled.div`
    display: flex;
    flex-direction: column;
  `,
  Form: styled(Form)<{ $isPiiCustomerUnacknowledged: boolean }>`
   & > fieldset {
    padding: 0px;
    margin-top: ${props => props.$isPiiCustomerUnacknowledged ? '0px' : '20px'};
    border: none;

    @media ${screenSize.small} {
      padding: 0px;
    }

    & > div {
      border: none;
    }
   }
  `,
  Explanation: styled.p`
    margin-top: 13px;
  `
}
